import { useEffect, useRef, useState } from "react";
import { RotateCcw, Send, Save, Mail } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Loader } from "@/components/common/Loader";
import { Field, TextInput } from "@/components/common/Forms";
import { api, errMsg, useFetch } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

function TemplateList({ items, sel, onSelect }) {
  const groups = [...new Set(items.map((t) => t.group))];
  return (
    <div className="space-y-6" data-testid="email-template-list">
      {groups.map((g) => (
        <div key={g}>
          <p className="eyebrow mb-2">{g}</p>
          <div className="space-y-1">
            {items.filter((t) => t.group === g).map((t) => (
              <button key={t.key} data-testid={`email-template-${t.key}`} onClick={() => onSelect(t.key)}
                className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition-colors ${sel === t.key ? "bg-gold/15 text-white shadow-[inset_2px_0_0_#D4AF37]" : "text-warm-text hover:text-white hover:bg-white/[0.04]"}`}>
                <span className="block">{t.label}</span>
                <span className="block text-[10px] uppercase tracking-[0.14em] mt-0.5 opacity-70">A: {t.to}{t.customized ? " · modificata" : ""}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function VarChips({ vars, onInsert }) {
  const keys = Object.keys(vars);
  if (!keys.length) return <p className="text-xs text-warm-text">Questa email non ha dati variabili.</p>;
  return (
    <div className="flex flex-wrap gap-2" data-testid="email-vars">
      {keys.map((k) => (
        <button key={k} type="button" title={vars[k]} onClick={() => onInsert(`{${k}}`)} data-testid={`email-var-${k}`}
          className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs text-gold-light hover:bg-gold/20 transition-colors">
          {`{${k}}`} <span className="text-warm-text ml-1">{vars[k]}</span>
        </button>
      ))}
    </div>
  );
}

function Preview({ tkey, form }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    const id = setTimeout(() => {
      api.post(`/admin/email-templates/${tkey}/preview`, form).then((r) => setP(r.data)).catch((e) => setP({ error: errMsg(e) }));
    }, 400);
    return () => clearTimeout(id);
  }, [tkey, form]);
  if (!p) return <Loader />;
  if (p.error) return <p data-testid="email-preview-error" className="text-sm text-[#F6A496]">{p.error}</p>;
  return (
    <div>
      <p className="text-xs text-warm-text mb-2">Oggetto: <span data-testid="email-preview-subject" className="text-white">{p.subject}</span></p>
      <iframe title="Anteprima email" data-testid="email-preview-frame" srcDoc={p.html} className="w-full h-[560px] rounded-xl border border-white/10 bg-black" />
      <p className="text-[11px] text-warm-text mt-2">Anteprima con dati di esempio.</p>
    </div>
  );
}

function Editor({ t, onSaved }) {
  const [form, setForm] = useState(t.current);
  const [busy, setBusy] = useState("");
  const bodyRef = useRef(null);
  useEffect(() => { setForm(t.current); }, [t]);
  const insert = (v) => {
    const el = bodyRef.current;
    const pos = el ? el.selectionStart : form.body.length;
    setForm({ ...form, body: form.body.slice(0, pos) + v + form.body.slice(pos) });
  };
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "save") { await api.put(`/admin/email-templates/${t.key}`, form); toast.success("Email salvata: da ora parte con il nuovo testo"); onSaved(); }
      if (kind === "reset") {
        if (!window.confirm("Ripristinare il testo originale di questa email?")) return;
        await api.delete(`/admin/email-templates/${t.key}`); toast.success("Testo originale ripristinato"); onSaved();
      }
      if (kind === "test") { const r = await api.post(`/admin/email-templates/${t.key}/test`, form); toast.success(`Email di prova inviata a ${r.data.sent_to}`); }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid gap-8 xl:grid-cols-2" data-testid="email-editor">
      <div className="space-y-5">
        <div>
          <p className="font-serif text-2xl">{t.label}</p>
          {t.customized && <p className="text-xs text-warm-text mt-1">Modificata il {fmtDateTime(t.updated_at)}{t.updated_by ? ` da ${t.updated_by}` : ""}</p>}
        </div>
        <Field label="Oggetto"><TextInput data-testid="email-subject-input" value={form.subject} onChange={set("subject")} /></Field>
        <Field label="Titolo"><TextInput data-testid="email-title-input" value={form.title} onChange={set("title")} /></Field>
        <Field label="Testo" hint="Lascia una riga vuota per iniziare un nuovo paragrafo. **parola** diventa grassetto.">
          <Textarea ref={bodyRef} data-testid="email-body-input" rows={10} value={form.body} onChange={set("body")} className="bg-ivory-deep rounded-xl" />
        </Field>
        <Field label="Dati che puoi inserire (tocca per aggiungerli al testo)"><VarChips vars={t.vars} onInsert={insert} /></Field>
        {(t.default.button || form.button) && <Field label="Testo del pulsante"><TextInput data-testid="email-button-input" value={form.button} onChange={set("button")} /></Field>}
        <div className="flex flex-wrap gap-3">
          <button data-testid="email-save-btn" disabled={!!busy} onClick={() => run("save")} className="btn-gold"><Save className="w-4 h-4" /> {busy === "save" ? "Salvataggio…" : "Salva"}</button>
          <button data-testid="email-test-btn" disabled={!!busy} onClick={() => run("test")} className="btn-ghost"><Send className="w-4 h-4" /> Invia una prova a me</button>
          {t.customized && <button data-testid="email-reset-btn" disabled={!!busy} onClick={() => run("reset")} className="btn-ghost"><RotateCcw className="w-4 h-4" /> Ripristina originale</button>}
        </div>
      </div>
      <Preview tkey={t.key} form={form} />
    </div>
  );
}

export default function AdminEmails() {
  const { data, loading, reload } = useFetch("/admin/email-templates");
  const [sel, setSel] = useState("benvenuto");
  const t = data?.find((x) => x.key === sel);
  return (
    <div>
      <PageHeader eyebrow="Comunicazioni" title="Email automatiche" subtitle="Cambia oggetto, titolo, testo e pulsante di ogni email che l'app invia. La grafica nero e oro resta sempre la stessa." />
      {loading || !data ? <Loader /> : (
        <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
          <div className="lux-card p-4 lg:sticky lg:top-6 self-start"><TemplateList items={data} sel={sel} onSelect={setSel} /></div>
          <div className="lux-card p-5 sm:p-8 min-w-0">{t ? <Editor key={t.key} t={t} onSaved={reload} /> : <p className="text-sm text-warm-text inline-flex items-center gap-2"><Mail className="w-4 h-4" /> Seleziona un'email</p>}</div>
        </div>
      )}
    </div>
  );
}
